<form novalidate #form="ngForm">
    @if (title) {
        <div class="mb-1 ml-3 text-base text-muted">{{ title | translate }}</div>
    }
    <kendo-grid
        class="rounded-lg border-0 shadow-md"
        [data]="localGridData"
        [skip]="localState.skip"
        [pageable]="pageable"
        [pageSize]="localState.take"
        [sortable]="sortable"
        [scrollable]="scrollable"
        [reorderable]="reorderable"
        [resizable]="resizable"
        [size]="gridSize"
        [height]="gridHeight"
        [hideHeader]="hideHeader"
        [loading]="isBusy"
        [sort]="sort"
        [rowClass]="rowClass"
        [navigable]="true"
        [selectable]="selectable"
        [kendoGridSelectBy]="selectBy"
        [(selectedKeys)]="selection"
        (dataStateChange)="onDataStateChanged($event)"
        (add)="onAdd($event, form)"
        (edit)="onEdit($event)"
        (cancel)="onCancel($event)"
        (save)="onSave($event)"
        (remove)="onRemove($event)"
        (excelExport)="onExcelExport($event)"
        (detailExpand)="onDetailExpanded($event)">
        @if (isSelectable) {
            <kendo-grid-checkbox-column [showSelectAll]="showSelectAll" [width]="40" />
        }
        @for (column of gridSettings?.columnsConfig; track column) {
            @if (column.isVisible) {
                <kendo-grid-column
                    [title]="column.title | translate"
                    [field]="column.field"
                    [width]="column.width"
                    [class]="column.class"
                    [format]="column.format"
                    [style]="column.style"
                    [hidden]="column.isHidden">
                    @if (column.columnHeaderTooltip) {
                        <ng-template kendoGridHeaderTemplate>
                            <div
                                class="flex w-full items-center justify-between gap-2"
                                kendoTooltip
                                title="{{ column.columnHeaderTooltip.text | translate }}">
                                {{ column.title | translate }}
                                <ng-container *ngIf="column.columnHeaderTooltip.showIcon">
                                    <ng-icon
                                        size="0.75rem"
                                        [class]="
                                            column.columnHeaderTooltip.iconClass || 'text-primary-foreground opacity-75'
                                        "
                                        [name]="column.columnHeaderTooltip.icon || 'faSolidCircleInfo'"></ng-icon>
                                </ng-container>
                            </div>
                        </ng-template>
                    }
                    @if (column.editTemplate) {
                        <ng-template kendoGridEditTemplate let-dataItem="dataItem">
                            <app-grid-edit-template [column]="column" [row]="dataItem" />
                        </ng-template>
                    }
                    <ng-template kendoGridCellTemplate let-dataItem="dataItem">
                        @if (column.isLink) {
                            <app-grid-cell-template
                                [column]="column"
                                [defaultViewMode]="defaultViewMode"
                                [parentRoute]="parentRoute"
                                [row]="dataItem" />
                        } @else if (column.type === 'datetime' || column.type === 'date') {
                            <app-grid-cell-template
                                [column]="column"
                                [defaultDateFormat]="defaultDateFormat"
                                [defaultDateTimeFormat]="defaultDateTimeFormat"
                                [row]="dataItem" />
                        } @else {
                            <app-grid-cell-template [column]="column" [row]="dataItem" />
                        }
                    </ng-template>
                </kendo-grid-column>
            }
        }
        @if (editMode) {
            <kendo-grid-command-column [width]="40">
                <ng-template kendoGridCellTemplate let-isNew="isNew" let-row>
                    @if (canEditRow(row)) {
                        <div class="flex h-full flex-row items-center gap-2">
                            @if (canClone) {
                                <app-button variant="primary" icon="faSolidClone" (clicked)="onClone(row, form)" />
                            }
                            @if (canEdit) {
                                <button
                                    kendoGridEditCommand
                                    kendoTooltip
                                    size="small"
                                    title="{{ 'Edit' | translate }}"
                                    [primary]="true">
                                    <ng-icon size="9px" name="faSolidPenToSquare" />
                                </button>
                                <button
                                    size="small"
                                    kendoGridSaveCommand
                                    kendoTooltip
                                    title="{{ 'Confirm' | translate }}">
                                    @if (isNew !== undefined) {
                                        <ng-icon size="9px" name="faSolidCheck" />
                                    }
                                </button>
                            }
                            @if (canEdit && canDelete) {
                                <button
                                    size="small"
                                    kendoGridRemoveCommand
                                    kendoTooltip
                                    title="{{ 'Delete' | translate }}">
                                    <ng-icon size="9px" name="faSolidTrash" />
                                </button>
                            }
                            <button size="small" kendoGridCancelCommand kendoTooltip title="{{ 'Cancel' | translate }}">
                                <ng-icon size="9px" name="faSolidBan" />
                            </button>
                        </div>
                    }
                </ng-template>
            </kendo-grid-command-column>
        }
        <ng-template *ngIf="showToolbar" kendoGridToolbarTemplate>
            @if (editMode && canEdit && canAddNew && !isEditing) {
                <button
                    class="btn btn-primary btn-md border-0 rounded-lg"
                    kendoTooltip
                    title="{{ 'Add' | translate }}"
                    kendoGridAddCommand>
                    <ng-icon name="faSolidPlus" />
                </button>
            }
            @for (section of buttons; track $index) {
                @if (['Actions', 'Export'].includes(section.label)) {
                    @for (button of section?.items; track $index) {
                        @if (resolveProperty(button?.isVisible)) {
                            <app-button
                                class="pr-2"
                                [variant]="button?.variant"
                                [disabled]="resolveProperty(button?.isDisabled)"
                                [tooltip]="button?.label.toString() | translate"
                                [icon]="button?.icon"
                                (clicked)="button?.onClick()">
                                @if (!resolveProperty(button?.iconOnly)) {
                                    <span class="leading-3">
                                        {{ button?.label.toString() | translate }}
                                    </span>
                                }
                            </app-button>
                        }
                    }
                }
            }
            <kendo-grid-spacer />
            <ng-content select="[toolbarCenter]" />
            <kendo-grid-spacer />
            @if (canExport) {
                <app-button
                    class="mr-1"
                    variant="excel"
                    icon="faSolidFileExcel"
                    tooltip="{{ 'Export to Excel' | translate }}"
                    (clicked)="exportToExcel()" />
            }
        </ng-template>
        @if (canExport) {
            <kendo-grid-excel fileName="Export.xlsx" />
        }
        <kendo-grid-messages
            pagerItems="{{ 'items' | translate }}"
            pagerItemsPerPage="{{ 'items per page' | translate }}"
            noRecords="{{ 'No records available' | translate }}"
            pagerOf="{{ 'of' | translate }}" />
    </kendo-grid>
</form>
